import { AnyAction } from 'redux';
import { GeneralCatalogItem } from 'data-layer-cr/dist/models';
import { setRequestStatus } from 'data-layer-cr/dist/network';
import { AsyncAppThunk, AsyncDispatch } from 'src/store/ThunkTypes';

// Network
import processRequest from 'src/services/processRequest';

// Models
import AdditionalBrokersModel from 'src/models/Catalogs/AdditionalBrokersModel';
import ModelsCarItem from 'src/models/Catalogs/ModelsCarItem';
import TransitInformation from 'src/models/Catalogs/TransitInformation';
import ProvinceItem from 'src/models/Catalogs/ProvinceItem';
import {
  REQUEST_ADDITIONAL_BROKERS,
  REQUEST_BRANDS,
  REQUEST_GENDERS,
  REQUEST_MODELS,
  REQUEST_PROVINCES,
  REQUEST_TRANSIT_INFO,
} from '../requestIdentifiers';

import {
  updateGenders,
  updateProvinces,
  updateTransitInfo,
  updateBrands,
  updateModels,
  updateAdditionalBrokers,
} from '../slice/catalogSlice';

/**
 * Request the list of genders from the catalog endpoint
 */
export const getGenders =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_GENDERS, 'requesting'));
    try {
      const data = await processRequest<GeneralCatalogItem[]>({
        method: 'GET',
        url: 'catalog/genders',
      });
      dispatch(updateGenders(data));
      return dispatch(setRequestStatus(REQUEST_GENDERS, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(REQUEST_GENDERS, 'failure'));
    }
  };

/**
 * Returns the list of provinces from the catalog endpoint
 */
export const getProvinces =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_PROVINCES, 'requesting'));
    try {
      const data = await processRequest<ProvinceItem[]>({
        method: 'GET',
        url: `Catalog/locations/states/${process.env.REACT_APP_COUNTRY_ID}`,
      });
      dispatch(updateProvinces(data));
      return dispatch(setRequestStatus(REQUEST_PROVINCES, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(REQUEST_PROVINCES, 'failure'));
    }
  };

/**
 * Requests the transit information
 */
export const getTransitInformation =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_TRANSIT_INFO, 'requesting'));
    try {
      const data = await processRequest<TransitInformation>({
        method: 'GET',
        url: 'Catalog/transit/information',
      });

      dispatch(updateTransitInfo(data));
      return dispatch(setRequestStatus(REQUEST_TRANSIT_INFO, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(REQUEST_TRANSIT_INFO, 'failure'));
    }
  };

/**
 * Request the list of car brands
 */
export const getBrands =
  (): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_BRANDS, 'requesting'));
    try {
      const data = await processRequest<GeneralCatalogItem[]>({
        method: 'GET',
        url: 'Catalog/vehicles/brands',
      });
      dispatch(updateBrands(data));
      return dispatch(setRequestStatus(REQUEST_BRANDS, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(REQUEST_BRANDS, 'failure'));
    }
  };

/**
 * Requests the list of models for the provided brand.
 *
 * @param brandId - The brand to filter with
 */
export const getModels =
  (brandId: number): AsyncAppThunk =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_MODELS, 'requesting'));
    try {
      const data = await processRequest<ModelsCarItem[]>({
        method: 'GET',
        url: `Catalog/vehicles/models/${brandId}`,
      });
      dispatch(updateModels(data));
      return dispatch(setRequestStatus(REQUEST_MODELS, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(REQUEST_MODELS, 'failure'));
    }
  };

export const getAdditionalBrokers =
  (parentLicense: string) =>
  async (dispatch: AsyncDispatch): Promise<AnyAction> => {
    dispatch(setRequestStatus(REQUEST_ADDITIONAL_BROKERS, 'requesting'));
    try {
      const response = await processRequest<AdditionalBrokersModel>({
        method: 'GET',
        url: `Catalog/additionalbrokers?filter[limit]=100&filter[where][Parent][eq]=${parentLicense}`,
      });
      dispatch(updateAdditionalBrokers(response.data));
      return dispatch(setRequestStatus(REQUEST_ADDITIONAL_BROKERS, 'success'));
    } catch (e) {
      return dispatch(setRequestStatus(REQUEST_ADDITIONAL_BROKERS, 'failure'));
    }
  };
